<template>
  <div class="wallet-page">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-start mb-3">
      <div>
        <h3 class="page-title mb-1">Баланс лицевого счета payton.kz</h3>
        <div class="page-subtitle">
          Всего активных записей: <span class="subtitle-strong">{{ balances.length }}</span>
        </div>
      </div>

      <div class="d-flex">
        <b-button variant="outline-light" class="btn-round mr-2" @click="loadAll">
          ↻ Обновить
        </b-button>
        <b-button variant="primary" class="cta-btn" @click="$router.push('/topup')">
          + Пополнить
        </b-button>
      </div>
    </div>

    <!-- Balances: crypto + fiat groups -->
    <template v-if="balances.length">
      <!-- Digital assets -->
      <div v-if="cryptoBalances.length" class="balance-group">
        <h4 class="group-title">Цифровые активы на балансе Payton</h4>
        <b-row>
          <b-col
            v-for="entry in cryptoBalances"
            :key="entry.balanceId"
            lg="4"
            md="6"
            class="mb-4"
          >
            <div class="wallet-card">
              <div class="wallet-header">
                <div class="currency-badge">
                  {{ entry.asset }}<span v-if="entry.network" class="net">{{ entry.network }}</span>
                </div>
                <small class="meta-muted">Баланс</small>
              </div>
              <div class="wallet-balance">
                {{ formatAmount(entry.available, entry.asset) }}
              </div>
              <div class="wallet-meta">
                <div>
                  <span class="label">В резерве по ордерам:</span>
                  <span class="value">{{ formatAmount(entry.locked, entry.asset) }}</span>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Fiat -->
      <div v-if="fiatBalances.length" class="balance-group mt-4">
        <h4 class="group-title">Фиатные средства на балансе Payton</h4>
        <b-row>
          <b-col
            v-for="entry in fiatBalances"
            :key="entry.balanceId"
            lg="4"
            md="6"
            class="mb-4"
          >
            <div class="wallet-card">
              <div class="wallet-header">
                <div class="currency-badge">{{ entry.asset }}</div>
                <small class="meta-muted">Баланс</small>
              </div>
              <div class="wallet-balance">
                {{ formatAmount(entry.available, entry.asset) }}
              </div>
              <div class="wallet-meta">
                <div>
                  <span class="label">В резерве по ордерам:</span>
                  <span class="value">{{ formatAmount(entry.locked, entry.asset) }}</span>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </template>

    <!-- Empty state -->
    <div v-else class="empty">
      <div class="empty-icon">👛</div>
      <div class="empty-title">Балансов пока нет</div>
      <div class="empty-subtitle">
        Чтобы продать криптовалюту — сначала пополните крипто-баланс.
        После подтверждения оператором актив появится здесь, и вы сможете его продать.
      </div>
      <b-button variant="primary" class="cta-btn mt-3" @click="$router.push('/topup')">
        + Пополнить баланс
      </b-button>
    </div>

    <!-- Deposits history -->
    <div class="mt-5" v-if="deposits.length">
      <h4 class="page-title mb-3" style="font-size: 18px;">История пополнений</h4>
      <div class="deposits-list">
        <div v-for="dep in deposits" :key="dep.depositId" class="dep-item">
          <div class="dep-row">
            <div>
              <span class="dep-id">#{{ dep.depositId }}</span>
              <span class="dep-status" :class="depositStatusCss(dep.status)">{{ depositStatusLabel(dep.status) }}</span>
              <span v-if="dep.source === 'MANUAL_CREDIT'" class="dep-source">Ручное начисление</span>
            </div>
            <div class="dep-date">{{ formatDate(dep.confirmedAt || dep.createdAt) }}</div>
          </div>
          <div class="dep-row dep-row-amount">
            <div class="dep-amount">{{ dep.amount != null ? formatAmount(dep.amount, dep.asset) : 'сумма не указана' }}</div>
          </div>
          <div v-if="dep.bankReference" class="dep-extra">Reference: <span class="mono">{{ dep.bankReference }}</span></div>
          <div v-if="dep.txHash" class="dep-extra">tx: <span class="mono">{{ dep.txHash }}</span></div>
          <div v-if="dep.operatorComment" class="dep-extra">Комментарий: {{ dep.operatorComment }}</div>
          <!-- Причину отказа клиенту не показываем: rejected_reason —
               служебный аудит-комментарий бэкофиса (клиент получает
               формулярное письмо об отклонении). -->
          <!-- Незавершённый крипто-черновик без TX Hash: даём завершить. -->
          <div v-if="isDraftAwaitingHash(dep)" class="dep-extra">
            <span class="dep-draft-hint">Заявка не завершена — не указан TX Hash.</span>
            <b-button size="sm" variant="primary" class="dep-resume-btn" @click="resumeDeposit(dep)">Указать TX Hash</b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain, getHeader } from '@/config/config'

export default {
  name: 'UserWallet',
  data() {
    return {
      balances: [],
      deposits: [],
    }
  },
  computed: {
    // Разделяем балансы на цифровые активы и фиат (USD).
    cryptoBalances() {
      return this.balances.filter((b) => (b.asset || '').toUpperCase() !== 'USD')
    },
    fiatBalances() {
      return this.balances.filter((b) => (b.asset || '').toUpperCase() === 'USD')
    },
  },
  methods: {
    // Незавершённый крипто-черновик: PENDING, ONCHAIN, без TX Hash.
    isDraftAwaitingHash(dep) {
      return dep
        && (dep.status || '').toUpperCase() === 'PENDING'
        && (dep.paymentMethod || '').toUpperCase() === 'ONCHAIN'
        && !dep.txHash
    },
    // Возврат к заявке для ввода TX Hash — открываем мастер на шаге 3.
    resumeDeposit(dep) {
      this.$router.push('/topup?deposit=' + dep.depositId)
    },
    loadAll() {
      this.fetchBalances()
      this.fetchDeposits()
    },
    fetchBalances() {
      axios.get(`${apiDomain}/funding/balances`, { headers: getHeader() })
        .then((res) => { this.balances = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.balances = [] })
    },
    fetchDeposits() {
      axios.get(`${apiDomain}/funding/deposits`, { headers: getHeader() })
        .then((res) => { this.deposits = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.deposits = [] })
    },
    formatAmount(amount, asset) {
      const fractionDigits = asset === 'USD' ? 2 : 8
      const value = Number(amount || 0)
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: asset === 'USD' ? 2 : 0,
        maximumFractionDigits: fractionDigits,
      }).format(value) + ' ' + (asset || '')
    },
    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('ru-RU', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    },
    depositStatusLabel(status) {
      switch ((status || '').toUpperCase()) {
        case 'PENDING':      return 'Ожидает'
        case 'CONFIRMED':    return 'Подтверждён'
        case 'REJECTED':     return 'Отклонён'
        case 'NOT_RECEIVED': return 'Не подтверждён'
        case 'FROZEN':       return 'Приостановлен'
        case 'EXPIRED':      return 'Истёк'
        default:             return status || '—'
      }
    },
    depositStatusCss(status) {
      switch ((status || '').toUpperCase()) {
        case 'PENDING':      return 'dep-status-pending'
        case 'CONFIRMED':    return 'dep-status-ok'
        case 'REJECTED':     return 'dep-status-no'
        case 'NOT_RECEIVED': return 'dep-status-no'
        case 'FROZEN':       return 'dep-status-pending'
        case 'EXPIRED':      return 'dep-status-muted'
        default:             return ''
      }
    },
  },
  created() {
    this.loadAll()
  },
}
</script>

<style scoped>
.wallet-page { margin-top: 18px; padding-bottom: 60px; }
.page-title { color: #FCFCFD; font-weight: 800; letter-spacing: -0.3px; margin-top: 6px; }
.group-title {
  color: #FCFCFD;
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(119,126,144,.18);
}
.page-subtitle { color: #B1B5C3; font-size: 13px; }
.subtitle-strong { color: #E6E8EC; font-weight: 700; }
.btn-round { border-radius: 12px; padding: 8px 14px; }
.cta-btn {
  border-radius: 12px;
  padding: 8px 16px;
  background: #efb914;
  border-color: #efb914;
  color: #1a1d22;
  font-weight: 700;
}
.cta-btn:hover { filter: brightness(1.05); }

.wallet-card {
  background: rgba(35,38,47,.55);
  border: 1px solid rgba(119,126,144,.18);
  border-radius: 16px;
  padding: 18px;
  height: 100%;
}
.wallet-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
}
.currency-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #FCFCFD;
  font-weight: 800;
  font-size: 18px;
}
.currency-badge .net {
  font-size: 10px;
  background: rgba(239,185,20,.18);
  color: #efb914;
  padding: 2px 7px;
  border-radius: 5px;
  letter-spacing: .02em;
  font-weight: 700;
}
.meta-muted { color: #777e90; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }

.wallet-balance {
  color: #FCFCFD;
  font: 800 22px/1.2 Inter;
  letter-spacing: -0.02em;
  margin-bottom: 14px;
}

.wallet-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #B1B5C3;
}
.wallet-meta .label { color: #777e90; }
.wallet-meta .value { color: #E6E8EC; font-weight: 600; margin-left: 4px; }

.empty {
  text-align: center;
  padding: 60px 20px;
  color: #B1B5C3;
}
.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-title { font: 700 18px/1 Inter; color: #FCFCFD; margin-bottom: 6px; }
.empty-subtitle { font-size: 13px; color: #777e90; max-width: 400px; margin: 0 auto; }

.deposits-list { display: flex; flex-direction: column; gap: 10px; }
.dep-item {
  background: rgba(35,38,47,.55);
  border: 1px solid rgba(119,126,144,.18);
  border-radius: 12px;
  padding: 12px 14px;
}
.dep-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.dep-row-amount { margin-top: 6px; }
.dep-id { font-size: 11px; font-weight: 700; color: #777e90; margin-right: 8px; }
.dep-status {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 5px;
  margin-right: 6px;
}
.dep-status-ok { background: rgba(16,185,129,0.15); color: #34D399; }
.dep-status-pending { background: rgba(245,158,11,0.15); color: #FBBF24; }
.dep-status-no { background: rgba(239,68,68,0.15); color: #F87171; }
.dep-status-muted { background: rgba(156,163,175,0.15); color: #9CA3AF; }
.dep-source {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 5px;
  background: rgba(96,165,250,0.15);
  color: #93C5FD;
}
.dep-date { font-size: 11px; color: #777e90; }
.dep-amount { color: #FCFCFD; font: 700 16px/1 Inter; }
.dep-method { font-size: 11px; color: #777e90; }
.dep-extra { font-size: 12px; color: #B1B5C3; margin-top: 4px; word-break: break-all; }
.dep-draft-hint { color: #FBBF24; margin-right: 10px; }
.dep-resume-btn { margin-top: 4px; }
.dep-extra .mono { font-family: ui-monospace, SFMono-Regular, monospace; color: #E6E8EC; }
.dep-extra-red { color: #F87171; }
</style>
